import React, { useMemo, useCallback } from "react";
import { RadioGroup, Radio, FormControlLabel } from "@mui/material";
import { Subtask, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import { ContentStatus } from "./SubtaskComponent";
import ContentStatusIndicator from "./ContentStatusIndicator";

interface ProjectCodingEditorProps {
  subtask: Subtask;
  filteredContent: Array<{
    content_id: string;
    text: string;
    time: string;
    level: string;
    type: typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE];
  }>;
  contentStatus?: ContentStatus | null;
  onContentSelected: (contentId: string, level: string, time: string) => void;
}

/**
 * ProjectCodingEditor Component
 * Handles editing/selecting project coding content in select mode
 * Uses radio buttons for single selection
 */
const ProjectCodingEditor: React.FC<ProjectCodingEditorProps> = ({
  subtask,
  filteredContent,
  contentStatus,
  onContentSelected
}) => {
  const currentId = useMemo(() => 
    (subtask.id && String(subtask.id).trim()) ? String(subtask.id).trim() : "", 
    [subtask.id]
  );
  const safeSelectedLevel = subtask.selectedContentLevel || "";
  
  // Create a map for O(1) lookup instead of O(n) find
  const contentMap = useMemo(() => {
    const map = new Map<string, typeof filteredContent[0]>();
    filteredContent.forEach(item => {
      if (item?.content_id) {
        map.set(String(item.content_id).trim(), item);
      }
    });
    return map;
  }, [filteredContent]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = e.target.value;
    if (currentId === selectedId) {
      onContentSelected("", safeSelectedLevel, "");
    } else {
      const selectedItem = contentMap.get(selectedId);
      onContentSelected(
        selectedId,
        safeSelectedLevel,
        selectedItem?.time || "0"
      );
    }
  }, [currentId, safeSelectedLevel, contentMap, onContentSelected]);

  return (
    <RadioGroup value={currentId} onChange={handleChange}>
      {filteredContent.map((item) => {
        if (!item?.content_id) return null; // Skip invalid items
        
        return (
          <div key={item.content_id} className="d-flex mb-2 align-items-center">
            <div className="d-flex align-items-start p-2 border rounded flex-grow-1">
              <FormControlLabel
                value={item.content_id.toString()}
                control={<Radio />}
                label={
                  <div style={{ marginLeft: "8px", fontSize: "12px", marginTop: "4px" }}>
                    {item.text || item.content_id}
                  </div>
                }
              />
            </div>
            <span className="d-flex align-items-center gap-2">
              <strong>{item?.time || "0"}m</strong>
              {item?.content_id && (
                <ContentStatusIndicator
                  contentId={String(item.content_id).trim()}
                  contentType={subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]}
                  contentStatus={contentStatus}
                />
              )}
            </span>
          </div>
        );
      })}
    </RadioGroup>
  );
};

export default ProjectCodingEditor;
